import { useDispatch, useSelector } from '@redux';
import { Tag } from 'antd';
import RestShow from 'containers/rest/Show';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import ShowContent from './ShowContent';
import crudActions from '@redux/crudActions';
import CancelModal from '../components/Modal/CancelModal';
import { ORDER_TTB_STATUS } from 'constants/ordersTabTryBuy';
import EditInvoiceModal from '../components/Modal/EditInvoiceModal';
import {
  createShippingNo,
  markAsComplete,
} from '@redux/ordersTabTryBuy/actions';
import ModalConfirmAction from '../components/Modal/ModalConfirmAction';
import EditPickupModal from '../components/Modal/EditPickupModal';
import { newCheckRole } from 'utils/tools';

const OPTIONS = {
  cancelOrder: [
    'AWAITING_CONFIRMATION',
    'CONFIRMED',
    'READY_TO_TRY',
    'DELIVERING',
  ], //1
  approveOrder: ['AWAITING_CONFIRMATION'], //2
  editPickupInformation: ['AWAITING_CONFIRMATION'], //3
  markAsComplete: ['READY_TO_TRY'], //4
  editNote: ['AWAITING_CONFIRMATION'], //5
  editInvoice: ['AWAITING_CONFIRMATION'], //6
  createShippingNo: ['CONFIRMED'],
};

const JOIN = [
  'orderBatch',
  'shop',
  'orderItems',
  'transferOrders||id,toId,status,fromWarehouseId,toWarehouseId',
  'orderShipping',
];

const createOption = (condition, label, key) =>
  condition ? { label, key } : null;

const Show = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [cancelVisible, setCancelVisible] = useState(false);
  const [editInvoiceVisible, setEditInvoiceVisible] = useState(false);

  const data = useSelector<DynamicObjectType>(
    (state) => state.ordersTabTryBuy.currentData,
  );
  const user = useSelector<DynamicObjectType>((state) => state.auth.data);

  const EXTRA_OPTIONS = useMemo(
    () =>
      [
        createOption(
          OPTIONS['cancelOrder'].includes(data?.statusOms),
          'Cancel order',
          'cancelOrder',
        ),
        createOption(
          OPTIONS['editPickupInformation'].includes(data?.statusOms) &&
            newCheckRole(
              '/orders/:id/edit-pickup-location',
              'PUT',
              user?.roles,
              user?.isSuperUser,
            ),
          'Edit pickup information',
          'editPickupInformation',
        ),
        createOption(
          OPTIONS['markAsComplete'].includes(data?.statusOms) &&
            newCheckRole(
              '/orders/:id/mark-as-completed',
              'PUT',
              user?.roles,
              user?.isSuperUser,
            ),
          'Mark as complete',
          'markAsComplete',
        ),
        createOption(
          OPTIONS['editInvoice'].includes(data?.statusOms),
          'Edit invoice',
          'editInvoice',
        ),
        createOption(
          OPTIONS['createShippingNo'].includes(data?.statusOms) &&
            newCheckRole(
              '/orders/:id/force-delivery',
              'PUT',
              user?.roles,
              user?.isSuperUser,
            ),
          'Create Shipping No',
          'createShippingNo',
        ),
      ].filter((option) => option !== null),
    [data?.statusOms],
  );

  const tempOrderStatus = ORDER_TTB_STATUS[data?.statusOms];

  const handleCancel = () => {
    setCancelVisible(true);
  };

  const openPickupModal = useCallback(() => {
    dispatch(
      crudActions.modal.toogleModal({
        ['editPickup']: {
          isOpen: true,
        },
      } as any),
    );
  }, [data?.id]);

  const openMarkAsCompleteModal = useCallback(() => {
    dispatch(
      crudActions.modal.toogleModal({
        ['markAsComplete']: {
          isOpen: true,
        },
      } as any),
    );
  }, [data?.id]);

  const openCreateShippingNoModal = useCallback(() => {
    dispatch(
      crudActions.modal.toogleModal({
        ['createShippingNo']: {
          isOpen: true,
        },
      } as any),
    );
  }, [data?.id]);

  const handleMarkAsComplete = useCallback(() => {
    return dispatch(markAsComplete({ data: { id: data?.id } }));
  }, [data?.id]);

  const handleCreateShippingNo = useCallback(() => {
    return dispatch(createShippingNo({ data: { id: data?.id } }));
  }, [data?.id]);

  const handleExtraOptions = useCallback(
    (e) => {
      switch (e.key) {
        case 'cancelOrder': {
          return handleCancel();
        }
        case 'editPickupInformation': {
          return openPickupModal();
        }
        case 'markAsComplete': {
          return openMarkAsCompleteModal();
        }
        case 'editInvoice': {
          return setEditInvoiceVisible(true);
        }
        case 'createShippingNo': {
          return openCreateShippingNoModal();
        }
      }
    },
    [data],
  );

  const handleParamsBreadcrumb = useMemo(() => {
    if (!ORDER_TTB_STATUS?.[data?.statusOms]) {
      return {
        title: 'All',
        href: '/orders-keep-and-try',
      };
    }
    return {
      title: t(ORDER_TTB_STATUS?.[data?.statusOms]?.text || ''),
      href: `/orders-keep-and-try?customLayout=${data?.statusOms}&filters=${encodeURIComponent(
        JSON.stringify(
          [
            {
              condition: 'and',
              key: 'statusOms',
              operator: 'is',
              value: data?.statusOms,
            },
          ],
          null,
          0,
        ),
      )}`,
    };
  }, [data?.statusOms]);

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'common.page.ordersTabTryBuy',
        href: '/orders-keep-and-try',
      },
      {
        title: handleParamsBreadcrumb?.title,
        href: handleParamsBreadcrumb?.href,
      },
      {
        title: (
          <span className="font-bold text-sm text-[--primary]">
            {`#${data?.orderNumber}`}
          </span>
        ),
      },
    ],
    [data?.statusOms, data?.orderNumber],
  );

  return (
    <>
      <RestShow
        model="ordersTabTryBuy"
        breadcrumbs={breadcrumbs?.filter((item) => !!item?.title)}
        extraBreadcrumb={
          <Tag
            color={ORDER_TTB_STATUS[data?.statusOms]?.background}
            style={{ borderRadius: 5, padding: '5px 10px', border: 'none' }}
          >
            <div
              style={{
                color: ORDER_TTB_STATUS[data?.statusOms]?.color,
              }}
            >
              {t(tempOrderStatus?.text)}
            </div>
          </Tag>
        }
        extraOptions={EXTRA_OPTIONS}
        handleExtraOptions={handleExtraOptions}
        apiOptions={{
          extraParams: {
            join: JOIN,
          },
          isRequestApi: true,
        }}
      >
        <ShowContent data={data} />
      </RestShow>
      <CancelModal
        visible={cancelVisible}
        onCancel={() => setCancelVisible(false)}
        {...data}
      />
      <EditInvoiceModal
        visible={editInvoiceVisible}
        onCancel={() => setEditInvoiceVisible(false)}
      />
      <ModalConfirmAction
        keyModal="markAsComplete"
        title="ordersTabTryBuy.detail.markAsCompleted"
        description="ordersTabTryBuy.detail.markAsCompleteDescription"
        handleConfirm={handleMarkAsComplete}
        handleCancel={() =>
          dispatch(
            crudActions.modal.toogleModal({
              ['markAsComplete']: { isOpen: false },
            } as any),
          )
        }
        callback={() => window.location.reload()}
      />
      <ModalConfirmAction
        keyModal="createShippingNo"
        title="ordersTabTryBuy.detail.createShippingNo"
        description="ordersTabTryBuy.detail.createShippingNoDescription"
        handleConfirm={handleCreateShippingNo}
        handleCancel={() =>
          dispatch(
            crudActions.modal.toogleModal({
              ['createShippingNo']: { isOpen: false },
            } as any),
          )
        }
        callback={() => window.location.reload()}
      />
      <EditPickupModal />
    </>
  );
};

export default Show;
